import { isBetterAuthApiKey } from '~/lib/johkuApiAuth'
import {
  beginSessionRecovery,
  isSessionRecoverySuppressed,
  tryRefreshBetterAuthSession
} from '~/lib/sessionRecovery'

const KEEP_ALIVE_MS = 60 * 60 * 1000
const MIN_VISIBILITY_REFRESH_MS = 5 * 60 * 1000

type StoreLike = {
  state: { shopId?: string | null, user?: unknown, sessionNeedsRecovery?: boolean }
  getters: { apiKey?: string | null }
  dispatch: (action: string, payload?: unknown) => unknown
}

type KeepAliveRuntime = {
  timer: number
  onVisibility: () => void
}

declare global {
  // eslint-disable-next-line no-var
  var __johkuSessionKeepAlive: KeepAliveRuntime | undefined
}

// Periodic better-auth get-session so expiresAt / cookie Max-Age actually slide
// while a POS tab stays open. Visibility refetch covers tablets that sleep.
export default defineNuxtPlugin((nuxtApp) => {
  if (!import.meta.client) {
    return
  }

  const previous = globalThis.__johkuSessionKeepAlive
  if (previous) {
    window.clearInterval(previous.timer)
    document.removeEventListener('visibilitychange', previous.onVisibility)
  }

  const store = nuxtApp.$store as StoreLike
  let lastRefreshAt = 0
  let inFlight = false

  async function refreshSession (reason: 'interval' | 'visible') {
    if (inFlight || isSessionRecoverySuppressed() || store.state.sessionNeedsRecovery) {
      return
    }
    if (!store.state.user || !store.state.shopId) {
      return
    }
    if (!isBetterAuthApiKey(store.getters.apiKey)) {
      return
    }
    const now = Date.now()
    if (reason === 'visible' && lastRefreshAt && now - lastRefreshAt < MIN_VISIBILITY_REFRESH_MS) {
      return
    }
    inFlight = true
    try {
      const result = await tryRefreshBetterAuthSession(store)
      lastRefreshAt = Date.now()
      if (result.status === 'expired') {
        beginSessionRecovery(store)
      }
    } finally {
      inFlight = false
    }
  }

  const timer = window.setInterval(() => {
    void refreshSession('interval')
  }, KEEP_ALIVE_MS)

  const onVisibility = () => {
    if (document.visibilityState === 'visible') {
      void refreshSession('visible')
    }
  }
  document.addEventListener('visibilitychange', onVisibility)

  globalThis.__johkuSessionKeepAlive = { timer, onVisibility }
})
